// Profile Setup / Onboarding Component
import { initializeApp } from '../app';
import { showToast } from '../utils/notifications';
import gsap from 'gsap';

export function renderProfileSetup(appState) {
  const container = document.createElement('div');
  container.className = 'profile-setup container';
  container.style.cssText = `
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-xl);
  `;

  const formCard = document.createElement('div');
  formCard.className = 'card-brutal';
  formCard.style.cssText = `max-width: 600px; width: 100%;`;

  formCard.innerHTML = `
    <div class="card-header">
      <h1 class="card-title text-gradient">Welcome to STEM Quest! ⚡</h1>
      <p class="card-subtitle">Let's get to know you before starting your learning journey</p>
    </div>
    
    <form id="profile-form">
      <div class="form-group">
        <label class="form-label" for="name">Your Name *</label>
        <input 
          type="text" 
          id="name" 
          class="form-input" 
          placeholder="Enter your name" 
          required
        />
      </div>
      
      <div class="form-group">
        <label class="form-label" for="grade">Grade Level *</label>
        <select id="grade" class="form-select" required>
          <option value="">Select your grade</option>
          <option value="6">Grade 6</option>
          <option value="7">Grade 7</option>
          <option value="8">Grade 8</option>
          <option value="9">Grade 9</option>
          <option value="10">Grade 10</option>
          <option value="11">Grade 11</option>
          <option value="12">Grade 12</option>
          <option value="college">College / University</option>
        </select>
      </div>
     
      <div class="form-group">
        <label class="form-label">STEM Interests (Select all that apply) *</label>
        <div class="interest-grid" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--spacing-md);">
          <label class="interest-option" style="display: flex; align-items: center; gap: var(--spacing-sm); cursor: pointer;">
            <input type="checkbox" name="interests" value="mathematics" class="form-checkbox" />
            <span>📐 Mathematics</span>
          </label>
          <label class="interest-option" style="display: flex; align-items: center; gap: var(--spacing-sm); cursor: pointer;">
            <input type="checkbox" name="interests" value="physics" class="form-checkbox" />
            <span>🔬 Physics</span>
          </label>
          <label class="interest-option" style="display: flex; align-items: center; gap: var(--spacing-sm); cursor: pointer;">
            <input type="checkbox" name="interests" value="chemistry" class="form-checkbox" />
            <span>⚗️ Chemistry</span>
          </label>
          <label class="interest-option" style="display: flex; align-items: center; gap: var(--spacing-sm); cursor: pointer;">
            <input type="checkbox" name="interests" value="computer-science" class="form-checkbox" />
            <span>💻 Computer Science</span>
          </label>
        </div>
      </div>
      
      <div class="form-group">
        <label class="form-label" for="career-goal">Career Aspiration (Optional)</label>
        <input 
          type="text" 
          id="career-goal" 
          class="form-input" 
          placeholder="e.g., Software Engineer, Data Scientist, Physicist" 
        />
      </div>
      
      <button type="submit" class="btn btn-neon btn-large" style="width: 100%; margin-top: var(--spacing-lg);">
        Start My STEM Journey 🚀
      </button>
    </form>
  `;

  container.appendChild(formCard);

  // Handle form submission
  setTimeout(() => {
    const form = container.querySelector('#profile-form');
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const name = form.querySelector('#name').value;
      const grade = form.querySelector('#grade').value;
      const careerGoal = form.querySelector('#career-goal').value;
      const interests = Array.from(form.querySelectorAll('input[name="interests"]:checked'))
        .map(checkbox => checkbox.value);

      if (interests.length === 0) {
        showToast('Please select at least one STEM interest', 'warning');
        return;
      }

      // If using backend, update profile via API
      if (appState.useBackend && appState.isAuthenticated) {
        try {
          const { authAPI } = await import('../services/api.js');
          const response = await authAPI.updateProfile({
            grade: parseInt(grade),
            interests,
            careerAspiration: careerGoal
          });

          if (response.success) {
            // Update app state with backend user data
            appState.user = response.data.user;
            appState.user.name = name; // Backend might not have name field, add it

            showToast(`Welcome aboard, ${name}! Profile updated! 🎉`, 'success');

            // Animate exit and initialize app
            gsap.to(formCard, {
              opacity: 0,
              y: -30,
              duration: 0.4,
              onComplete: () => {
                initializeApp(appState);
              }
            });
          } else {
            showToast(response.message || 'Failed to update profile', 'error');
          }
        } catch (error) {
          showToast('Error updating profile: ' + error.message, 'error');
          console.error('Profile update error:', error);
        }
      } else {
        // localStorage mode
        const user = {
          id: `user-${Date.now()}`,
          name,
          grade,
          interests,
          careerGoal,
          totalXP: 0,
          level: 1,
          badges: [],
          completedQuests: [],
          skillProfile: {},
          createdAt: new Date().toISOString()
        };

        // Save to localStorage
        localStorage.setItem('stem-quest-user', JSON.stringify(user));

        // Update app state
        appState.user = user;
        appState.isAuthenticated = true;

        // Animate exit and initialize app
        gsap.to(formCard, {
          opacity: 0,
          y: -30,
          duration: 0.4,
          onComplete: () => {
            showToast(`Welcome aboard, ${name}! Let's start learning! 🎉`, 'success');
            initializeApp(appState);
          }
        });
      }
    });
  }, 100);

  return container;
}
